import classNames from 'classnames';
import React from 'react';

import { MaybeUndef } from '../../../types/system';
import { validateJson } from '../../../utils/json/json-schema-validation';
import { ModalMessage } from '../../common';
import { JSON_EDITOR_GUTTERS, JsonEditor } from '../../json-editor/json-editor';

import { Footer } from './footer/footer';
import styles from './modal-json-editable.scss';
import { Editor } from 'codemirror';
import { i18n } from '../../../utils';

interface Props {
  title: React.ReactNode;
  isOpen: boolean;
  jsonString: string | undefined;
  jsonSchema?: object;

  onClose(): void;

  onSubmit(json: string | undefined): void;
}

interface State {
  isValid: MaybeUndef<boolean>;
  jsonString: string | undefined;
}

// TODO: codeMirror is refreshed after modal is opened and anitaion ends.
// Now this is implemented via timemout. Implement via animation end event
export class ModalJsonEditable extends React.Component<Props, State> {
  static defaultProps = {
    jsonString: '',
  };
  public state: State;
  public refreshTimeout: number;
  public codeMirrorInstance: Editor;

  constructor(props: Props) {
    super(props);

    this.state = {
      isValid: undefined,
      jsonString: props.jsonString,
    };
  }

  public refreshAfterAnimationEnds() {
    this.refreshTimeout = window.setTimeout(() => {
      if (this.codeMirrorInstance) {
        this.codeMirrorInstance.refresh();
      }
    }, 300);
  }

  public componentWillUnmount() {
    window.clearTimeout(this.refreshTimeout);
  }

  public onCodeMirrorInstance(instance: Editor) {
    this.codeMirrorInstance = instance;
    if (instance) {
      // Workaround. More info see here https://github.com/scniro/react-codemirror2/issues/100
      instance.setOption('gutters', JSON_EDITOR_GUTTERS);
    }
  }

  public render() {
    const { title, onClose, isOpen, jsonString, jsonSchema } = this.props;
    const { isValid } = this.state;

    return (
      <ModalMessage
        isOpen={isOpen}
        title={title}
        confirmText={i18n.t(
          'components.json-editor-with-toolbar.expand-modal.save',
        )}
        cancelText={i18n.t(
          'components.json-editor-with-toolbar.expand-modal.cancel',
        )}
        width="800px"
        onCancel={onClose}
        onSubmit={this.handleSaveClick.bind(this)}
        onAfterOpen={this.refreshAfterAnimationEnds.bind(this)}
        withForm={false}
      >
        <div className={styles.modal}>
          <JsonEditor
            json={jsonString}
            error={isValid === false}
            readOnly={false}
            onInstanceReceive={this.onCodeMirrorInstance.bind(this)}
            className={classNames(styles.jsonEditorWrapper, {
              [styles.validBorder]: isValid === true,
            })}
            onChange={this.handleValueChange.bind(this)}
            jsonSchema={jsonSchema}
          />

          <Footer
            className={styles.footer}
            onValidateClick={this.validateAndUpdateState.bind(this)}
            onCancelClick={onClose}
            onSaveClick={this.handleSaveClick.bind(this)}
            isValidButtonDisabled={isValid !== undefined}
          />
        </div>
      </ModalMessage>
    );
  }

  private handleValueChange(value: string) {
    this.setState({
      isValid: undefined,
      jsonString: value,
    });
  }

  private validate(): boolean {
    return validateJson(this.state.jsonString || '', this.props.jsonSchema);
  }

  private handleSaveClick() {
    const { isValid } = this.state;

    if (isValid === true) {
      this.props.onSubmit(this.state.jsonString);
      return;
    }

    if (this.validate()) {
      this.props.onSubmit(this.state.jsonString);
      return;
    }

    this.setState({
      isValid: false,
    });
  }

  private validateAndUpdateState() {
    const { isValid } = this.state;

    if (isValid !== undefined) {
      return;
    }

    this.setState({
      isValid: this.validate(),
    });
  }
}
